import { useMemo } from "react";
import { DeleteIcon } from '@panwds/icons';
import { Button, Tooltip, TruncateText, useModal } from "@panwds/react-ui";
import { useCallback, useEffect, useState } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { usePermissions, useTranslate } from '../../customHooks';
import { Table } from "../../components/PANWDSElements/Table";
import { ColumnOptions, Data, RowInstance } from "@panwds/react-table";
import { SelectFilter } from "@panwds/react-filters";
import { FirewallCreate } from "./FirewallCreate";
import { useGetFirewallsQuery, useDeleteFirewallMutation } from "../../redux/services/firewalls-service";
import { ApplicationConfigManager, SupportedRegion } from "../../types";
import { ConfirmationDialog, PANWDSBannerContainer, toast } from "../../components";
import { FIREWALL_STATE_FILTERS, getFirewallStateDisplay, prepareData } from "./firewallsUtil";
import { find, get } from "lodash";
import { RouteUri } from "../../routeUri";
import { FirewallCreateNotifModal } from "./Components/Create";
import { checkCSPBanner } from "../../utils";
import { useAppSelector } from "../../app/hooks";
import { Firewalls } from "../../interfaces/interfaces";

type PanelType = "" | "createFirewall";

const FirewallList = () => {
  const translate = useTranslate();
  const [sidePanel, setSidePanel] = useState<PanelType>("");
  const { permissions } = usePermissions();
  const history = useHistory();
  const reduxState = useAppSelector((state) => state);

  const {
    openModal: openFWCreateModal
  } = useModal(FirewallCreateNotifModal);

  const regionItems = ApplicationConfigManager.getInstance().getSupportedRegions().map((region: SupportedRegion) => ({
    children: region.RegionDisplayName,
    value: region.RegionCode
  }));

  useEffect(() => {
    if (permissions && !permissions?.ListFirewalls) {
      toast.warning(translate("permissions.cantView"), { toastId: "list-firewall" });
    }
  }, [permissions]);

  const { data: firewalls, error, isLoading, isFetching, refetch } = useGetFirewallsQuery({ Region: regionItems[0]?.value, MaxResults: 50, Describe: true }, {
    // Keep the query alive to receive updates
    skip: !permissions?.ListFirewalls,
    refetchOnMountOrArgChange: false,
    refetchOnReconnect: false,
  });


  if (error) {
    toast.error(error?.error, { toastId: "list-firewall" });
  }

  const [deleteFirewallMutation, { isLoading: isDeleting, isSuccess }] = useDeleteFirewallMutation();

  useEffect(() => {
    checkCSPBanner({ translate, reduxState });
  }, [reduxState.support.cspEnable]);

 
  const filterConfig = [
    {
      Component: SelectFilter,
      context: {
        enableMultiSelect: true,
        items: Object.entries(FIREWALL_STATE_FILTERS).map(([key, value]) => {
          return { value: key, children: value };
        })
      },
      label: translate('resources.firewallsV2.firewallStatus'),
      name: 'Status.FirewallStatus',
      static: true,
    },
    {
      Component: SelectFilter,
      context: {
        enableMultiSelect: true,
        items: regionItems,
      },
      label: translate('resources.firewallsV2.region'),
      name: 'Firewall.Region',
      static: true,
    },
    {
      Component: SelectFilter,
      context: {
        enableMultiSelect: true,
        items: [{
          value: 'Local Rulestack'
        }, {
          value: 'Strata Cloud Manager'
        }, {
          value: 'Panorama'
        }],
      },
      label: translate('resources.firewallsV2.policyManager'),
      name: 'Firewall.PolicyManager',
      static: true,
      comparator: (
        item: string,
        itemVal: string[],
        row: Record<string, unknown>
      ) => {
        return itemVal?.some(keyword => get(row, item).includes(keyword))
      },
    },
  ];

  const batchActions = useCallback((selectedRows: Data[]) => {
    return {
      rightControl: (permissions?.CreateFirewall &&
        <>
          <Button
            dataTestId="create-firewall"
            onClick={() => setSidePanel('createFirewall')}
            appearance="primary"
          >
            {translate(`resources.firewallsV2.create`)}
          </Button>
        </>
      )
    };
  }, []);

  const updateFirewall = useCallback((rowProps, event) => {
    if (!permissions?.DescribeFirewall) {
      return;
    }
    sessionStorage.setItem("fwEditActiveTab", "overview");
    history.push(RouteUri.NGFirewallEdit
      .replace(":firewallname", rowProps.original.Firewall.FirewallId)
      + "?region=" + rowProps.original.Firewall.Region);
  }, [history, permissions]);

  const columns = useMemo<ColumnOptions<any>[]>(
    () => [
      {
        accessor: 'Firewall.FirewallId',
        Header: translate('resources.firewallsV2.firewallId'),
        minWidth: 250,
        Cell: ({ row }: { row: RowInstance<object> }) => {
          return permissions?.DescribeFirewall ? <span className='tw-text-blue-600 tw-hover:text-blue-600 tw-cursor-pointer'
            onClick={(evt) => updateFirewall(row, evt)}>
            {row?.original?.Firewall.FirewallId}
          </span> :
            <span>{row?.original?.Firewall.FirewallId}</span>
        }
      },
      {
        accessor: 'Firewall.FirewallName',
        Header: translate('resources.firewallsV2.firewallName'),
        minWidth: 200,
        Cell: ({ value }: { value: string }) => {
          return <TruncateText>{value}</TruncateText>;
        }
      },
      {
        accessor: 'Status.FirewallStatus',
        Header: translate(`resources.firewallsV2.firewallStatus`),
        Cell: ({ row }: { row: RowInstance<object> }) => {
          { return getFirewallStateDisplay(row?.original?.Status) }
        }
      },
      {
        accessor: 'Firewall.Region',
        Header: translate(`resources.firewallsV2.region`),
        Cell: ({ value }: { value: string }) => {
          const region = find(regionItems, (region) => (region.value === value));
          return (region) ? region['children'] : value || '';
        }
      },
      {
        accessor: 'Firewall.PolicyManager',
        Header: translate(`resources.firewallsV2.policyManager`),
        minWidth: 250,
        Cell: ({ value }: { value: string }) => {
          return <TruncateText>{value}</TruncateText>;
        }
      },
      {
        accessor: 'Endpoints',
        Header: translate(`resources.firewallsV2.endpoints`),
        Cell: ({ row }: { row: RowInstance<object> }) => {
          return row?.original?.Firewall?.Endpoints?.length || 0;
        }
      },
    ],
    []
  );
  //show delete action column only of user has firewall delete permission
  if (permissions?.DeleteFirewall) {
    columns.push({
      accessor: 'actions',
      Header: translate(`generic.actions`),
      Cell: ({ row }: { row: RowInstance<object> }) => {
        return (
          <>
            <Tooltip label={translate(`resources.firewallsV2.delete`)}>
              <button className="tw-mr-2 tw-border-none tw-bg-transparent"
                onClick={(evt) => deleteFirewall(row?.original?.Firewall)}
              // disabled={isDeleting || row?.original?.Status?.FirewallStatus === "DELETING"}
              >
                <DeleteIcon
                  className="tw-text-zinc-400 dark:tw-text-gray-600 dark:group-hover:tw-text-gray-400 tw-cursor-pointer"
                  size="sm"
                  data-testid="deployment-list-delete-action"
                />
              </button>
            </Tooltip>
          </>
        );
      },
      sticky: true,
    })
  }

  const handleCreateCallback = async (newFirewallData?: Firewalls) => {
    if (newFirewallData) {
      openFWCreateModal({
        fwId: newFirewallData?.FirewallId || undefined,
        region: newFirewallData?.Region || undefined
      });
    } else {
      // Otherwise, refetch the list of firewalls
      await refetch();
    }
  };

  const { openModal, closeModal } = useModal(ConfirmationDialog);

  const handleDeleteConfirmation = useCallback(async (data) => {
    try {
      await deleteFirewallMutation({ payload: data }).unwrap()
      closeModal();
    }
    catch (error: any) {
      toast.error(`${error?.code}: ${error?.error}`, { toastId: "delete-firewall" });
      closeModal();
    }
  }, []);

  const deleteFirewall = (data) => {
    try {
      openModal({
        handleActionCallback: handleDeleteConfirmation,
        title: translate('resources.firewallsV2.deleteFirewallConfirmationTitle'),
        message: translate('resources.firewallsV2.deleteFirewallMessage'),
        saveButtonLabel: translate('generic.delete'),
        record: data,
      });
    } catch (error: any) {
      toast.error(`${error?.code}: ${error?.error}`, { toastId: "delete-firewall" });
    }
  };

  return (
    <>
      <PANWDSBannerContainer />
      <div className="tw-overflow-table-container tw-flex tw-flex-col">
        <Table
          tableComponentData={prepareData(firewalls?.Firewalls || [])}
          tableComponentColumns={columns}
          tableComponentEmptyStateProps={{
            heading: translate('resources.firewallsV2.emptyFirewallsTable'),
          }}
          tableComponentFilterBar={{
            hideAddFilter: true,
            filterConfig: filterConfig,
          }}
          tableBatchAction={batchActions}
          tableIsLoading={isLoading}
          tableisBackgroundFetching={firewalls?.NextToken || isFetching}
          tableTitle={translate('resources.firewallsV2.title')}
          tableOffsetHeight={150}
        />
        {sidePanel === 'createFirewall' && <FirewallCreate
          close={() => setSidePanel("")}
          panoramaRecord={[]}
          cloudManagerRecord={[]}
          record={undefined}
          handleCreateCallback={handleCreateCallback}
        />}
      </div>
    </>
  );
}

export default withRouter(FirewallList);
